import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSingleWsEvent } from "../store/modules/websocket";
import { centrifugeService } from "../axiosBase/centrifugeService";

/**
 * Hook for reading and mutating the typed WebSocket event store.
 *
 * Events are keyed by the `type` field in each server publication payload.
 * useWebSocketManager routes every publication into this map automatically,
 * so components only need to read and clear events here.
 *
 * Usage:
 *   const { wsEvents, clearWsEvent } = useWebSocketEventHandler();
 *
 *   useEffect(() => {
 *     if (!wsEvents?.order_status_update) return;
 *     // handle the event
 *     clearWsEvent({ eventType: "order_status_update" });
 *   }, [wsEvents?.order_status_update]);
 */
export const useWebSocketEventHandler = () => {
  const { wsEvents } = useSelector((state) => state.webSocketReducer);
  const dispatch = useDispatch();

  // Merge a single typed event into the Redux map
  const updateWsEvents = useCallback(
    ({ eventType, data }) => {
      if (!eventType) {
        console.warn("[WebSocket Event Updater] Invalid type key");
        return;
      }
      dispatch(setSingleWsEvent({ eventType, data }));
    },
    [dispatch],
  );

  // Null-out a single event by type once the component has handled it
  const clearWsEvent = useCallback(
    ({ eventType }) => {
      dispatch(setSingleWsEvent({ eventType, data: null }));
    },
    [dispatch],
  );

  // Publish a message to a Centrifuge channel
  const sendWsMessage = useCallback(
    (channel, data) => centrifugeService.publish(channel, data),
    [],
  );

  return {
    wsEvents,
    clearWsEvent,
    updateWsEvents,
    sendWsMessage,
  };
};
